import React, { useState } from 'react';
import { getClubLogoPublicUrl } from '../../services/clubLogoService';

/**
 * ClubLogo - Circular logo component for compact club display
 *
 * Shows the club's logo image in a circle. Falls back to up-to-two-letter
 * initials on a gradient background when no logo is set or the image fails.
 *
 * @param {Object} props
 * @param {string|null} props.logoImageUrl - Storage path (e.g., "{clubId}.jpg")
 * @param {string} [props.clubName] - Used for alt text and initials fallback
 * @param {'sm'|'md'|'lg'} [props.size] - 'sm' = 32px, 'md' = 48px (default), 'lg' = 80px
 * @param {string} [props.className] - Additional CSS classes
 */
const ClubLogo = ({
  logoImageUrl,
  clubName = '',
  size = 'md',
  className = ''
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const publicUrl = logoImageUrl ? getClubLogoPublicUrl(logoImageUrl) : null;
  const showImage = publicUrl && !imageError;

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-sm',
    lg: 'w-20 h-20 text-xl',
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;

  const initials = clubName
    ? clubName
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('')
    : '?';

  return (
    <div
      className={`relative rounded-full overflow-hidden bg-gradient-to-br from-primary/20 via-accent/20 to-info/20 flex items-center justify-center shrink-0 ${sizeClass} ${className}`}
    >
      {showImage ? (
        <>
          <img
            src={publicUrl}
            alt={clubName ? `${clubName} logo` : 'Club logo'}
            loading="lazy"
            onError={() => setImageError(true)}
            onLoad={() => setImageLoaded(true)}
            className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/20 to-info/20 animate-pulse" />
          )}
        </>
      ) : (
        <span className="font-semibold text-text-secondary select-none">{initials}</span>
      )}
    </div>
  );
};

export default ClubLogo;
